/**
 * Complete age-based profiles configuration with themes, content, and voice settings
 */

import { AgeProfile as BaseAgeProfile, AgeTheme, AgeAdaptiveContent } from '../types/age.types';

// Extended age profile for frontend usage
export interface AgeProfile extends BaseAgeProfile {
  name: string;
  theme: AgeTheme;
  content: AgeAdaptiveContent;
  ui: {
    appName: string;
    micButtonSize: string;
  };
}

// Complete age profiles with all data combined
export const completeAgeProfiles: Record<string, AgeProfile> = {
  '6-8': {
    // Base profile
    age_range: { min: 6, max: 8 },
    category: 'early-childhood',
    voice_name: 'shimmer', // Warm, gentle voice suitable for young children
    temperature: 0.6,
    speaking_rate: 0.8,
    silence_duration: 2000,
    system_prompt: 'You are a friendly, patient assistant talking to a young child (6-8 years old). Use simple words, be encouraging, and speak like you would to a child.',
    max_response_length: 20,
    vocabulary_level: 'elementary',
    
    // Frontend specific
    name: 'Young Learner',
    theme: {
      name: 'playful_primary',
      colors: {
        primary: '#FF6B6B',
        secondary: '#4ECDC4', 
        accent: '#FFE66D',
        background: '#F8F9FA',
        text: '#2C3E50'
      },
      typography: {
        fontFamily: 'font-playful',
        headingSize: 'text-2xl',
        bodySize: 'text-lg',
        buttonSize: 'text-xl'
      },
      spacing: {
        buttonPadding: 'px-6 py-4',
        componentMargins: 'mb-6',
        touchTargets: 'min-h-[60px]'
      },
      borderRadius: 'rounded-2xl',
      buttonSize: {
        width: 'w-28',
        height: 'h-32'
      },
      // CSS utility classes  
      primary: 'bg-pink-400',
      secondary: 'bg-teal-400', 
      accent: 'bg-yellow-400',
      background: 'bg-gradient-to-br from-pink-100 to-purple-100',
      text: 'text-gray-800'
    },
    content: {
      welcomeMessage: "Hi there! I'm your friendly talking buddy! Tell me how old you are so we can have fun chatting!",
      instructions: "Press the big round button and talk to me!",
      statusMessages: {
        connecting: "Getting ready to talk... 🎈",
        listening: "I'm listening to you! 👂",
        processing: "Thinking about what you said... 🤔",
        error: "Oops! Let's try again! 😊",
        microphonePermission: "I need to hear your voice! Please click 'Allow' when your computer asks."
      },
      buttonLabels: {
        startConversation: "Let's Talk!",
        startRecording: "Talk to Me!",
        stopRecording: "All Done!",
        clearHistory: "Start Over"
      },
      errorMessages: {
        general: "Oops! Something went wrong. Let's try again! 😊",
        microphone: "I can't hear you. Ask a grown-up to help with your microphone!",
        connection: "I can't connect right now. Ask a grown-up to check the internet!",
        audio: "The sound isn't working right. Let's ask a grown-up for help!"
      }
    },
    ui: {
      appName: "My Talking Friend",
      micButtonSize: "w-28 h-28"
    }
  },
  
  '9-11': {
    // Base profile
    age_range: { min: 9, max: 11 },
    category: 'middle-childhood',
    voice_name: 'coral', // Clear, friendly voice for middle elementary
    temperature: 0.7,
    speaking_rate: 0.9,
    silence_duration: 1800,
    system_prompt: 'You are a helpful assistant talking to a child (9-11 years old). Use age-appropriate language, be encouraging, and support their curiosity.',
    max_response_length: 30,
    vocabulary_level: 'middle_elementary',
    
    // Frontend specific
    name: 'Curious Explorer',
    theme: {
      name: 'bright_exploration',
      colors: {
        primary: '#3498DB',
        secondary: '#E74C3C',
        accent: '#F39C12',
        background: '#FFFFFF',
        text: '#34495E'
      },
      typography: {
        fontFamily: 'font-friendly',
        headingSize: 'text-xl',
        bodySize: 'text-base',
        buttonSize: 'text-lg'
      },
      spacing: {
        buttonPadding: 'px-5 py-3',
        componentMargins: 'mb-5',
        touchTargets: 'min-h-[50px]'
      },
      borderRadius: 'rounded-xl',
      buttonSize: {
        width: 'w-28',
        height: 'h-32'
      },
      // CSS utility classes
      primary: 'bg-blue-500',
      secondary: 'bg-green-500',
      accent: 'bg-orange-400',
      background: 'bg-gradient-to-br from-blue-100 to-green-100',
      text: 'text-gray-800'
    },
    content: {
      welcomeMessage: "Hello! I'm your AI voice assistant here to chat and help you learn. Let's start by knowing your age!",
      instructions: "Press the microphone button and speak clearly.",
      statusMessages: {
        connecting: "Connecting to voice assistant...",
        listening: "Recording your voice... 🎤",
        processing: "Understanding your message...",
        error: "Something went wrong. Let's retry!",
        microphonePermission: "Please allow microphone access so I can hear you speak."
      },
      buttonLabels: {
        startConversation: "Start Conversation",
        startRecording: "Start Recording",
        stopRecording: "Stop Recording", 
        clearHistory: "Clear History"
      },
      errorMessages: {
        general: "Something went wrong. Don't worry, let's try that again!",
        microphone: "I can't access your microphone. Please check your settings.",
        connection: "Connection problem. Please check your internet connection.",
        audio: "Audio isn't working properly. Try refreshing the page."
      }
    },
    ui: {
      appName: "Learning Companion",
      micButtonSize: "w-28 h-28"
    }
  },
  
  '12-14': {
    // Base profile
    age_range: { min: 12, max: 14 },
    category: 'early-adolescence',
    voice_name: 'ballad', // Modern, engaging voice for young teenagers
    temperature: 0.8,
    speaking_rate: 1.0,
    silence_duration: 1500,
    system_prompt: 'You are an AI assistant talking to a young teenager (12-14 years old). Be supportive, use appropriate vocabulary, and engage with their interests.',
    max_response_length: 40,
    vocabulary_level: 'middle_school',
    
    // Frontend specific
    name: 'Teen Assistant',
    theme: {
      name: 'modern_teen',
      colors: {
        primary: '#9B59B6',
        secondary: '#1ABC9C',
        accent: '#E67E22',
        background: '#F4F4F4',
        text: '#2C3E50'
      },
      typography: {
        fontFamily: 'font-modern',
        headingSize: 'text-lg',
        bodySize: 'text-base',
        buttonSize: 'text-base'
      },
      spacing: {
        buttonPadding: 'px-4 py-3',
        componentMargins: 'mb-4',
        touchTargets: 'min-h-[44px]'
      },
      borderRadius: 'rounded-lg',
      buttonSize: {
        width: 'w-28',
        height: 'h-32'
      },
      // CSS utility classes
      primary: 'bg-purple-500',
      secondary: 'bg-indigo-500',
      accent: 'bg-pink-400',
      background: 'bg-gradient-to-br from-purple-100 to-indigo-100',
      text: 'text-gray-800'
    },
    content: {
      welcomeMessage: "Welcome! I'm your AI conversation partner. Select your age to get started with voice chat.",
      instructions: "Use the microphone button to start our conversation.",
      statusMessages: {
        connecting: "Establishing connection...",
        listening: "Recording audio input... 🎵",
        processing: "Processing speech...",
        error: "Connection error. Please try again.",
        microphonePermission: "Microphone access required for voice conversation."
      },
      buttonLabels: {
        startConversation: "Begin Chat",
        startRecording: "Record",
        stopRecording: "Stop",
        clearHistory: "Clear"
      },
      errorMessages: {
        general: "An error occurred. Please try again.",
        microphone: "Microphone access denied. Check browser permissions.",
        connection: "Failed to connect. Verify your internet connection.",
        audio: "Audio system error. Try reloading the page."
      }
    },
    ui: {
      appName: "Voice Chat Assistant",
      micButtonSize: "w-28 h-28"
    }
  },
  
  '15-18': {
    // Base profile
    age_range: { min: 15, max: 18 },
    category: 'late-adolescence',
    voice_name: 'sage', // Professional, mature voice for older teenagers
    temperature: 0.8,
    speaking_rate: 1.0,
    silence_duration: 1200,
    system_prompt: 'You are an AI assistant talking to a teenager (15-18 years old). Be respectful, use mature language, and engage in meaningful conversations.',
    max_response_length: 60,
    vocabulary_level: 'adult',
    
    // Frontend specific
    name: 'Smart Assistant',
    theme: {
      name: 'professional_young_adult',
      colors: {
        primary: '#2C3E50',
        secondary: '#27AE60',
        accent: '#F39C12',
        background: '#FFFFFF',
        text: '#34495E'
      },
      typography: {
        fontFamily: 'font-professional',
        headingSize: 'text-base',
        bodySize: 'text-sm',
        buttonSize: 'text-sm'
      },
      spacing: {
        buttonPadding: 'px-3 py-2',
        componentMargins: 'mb-3',
        touchTargets: 'min-h-[40px]'
      },
      borderRadius: 'rounded',
      buttonSize: {
        width: 'w-28',
        height: 'h-32'
      },
      // CSS utility classes
      primary: 'bg-gray-700',
      secondary: 'bg-blue-600',
      accent: 'bg-green-500',
      background: 'bg-gradient-to-br from-gray-100 to-blue-100',
      text: 'text-gray-900'
    },
    content: {
      welcomeMessage: "Hi! I'm an AI assistant ready to chat with you. Please select your age for a personalized experience.",
      instructions: "Click the microphone to begin our voice conversation.",
      statusMessages: {
        connecting: "Connecting...",
        listening: "Listening...",
        processing: "Processing...",
        error: "Error occurred. Retry?",
        microphonePermission: "Microphone access needed."
      },
      buttonLabels: {
        startConversation: "Start",
        startRecording: "Record",
        stopRecording: "Stop",
        clearHistory: "Clear"
      },
      errorMessages: {
        general: "Error occurred. Please retry.",
        microphone: "Microphone access denied.",
        connection: "Connection failed.",
        audio: "Audio error occurred."
      }
    },
    ui: {
      appName: "AI Voice Assistant",
      micButtonSize: "w-28 h-28"
    }
  }
};

// Helper function to get age profile by age
export const getAgeProfile = (age: number): AgeProfile => {
  for (const [range, profile] of Object.entries(completeAgeProfiles)) {
    const [min, max] = range.split('-').map(Number);
    if (age >= min && age <= max) {
      return profile;
    }
  }
  
  // Default to oldest profile if age is higher than expected
  return completeAgeProfiles['15-18'];
};

// Export individual collections for backward compatibility
export const ageProfiles: Record<string, BaseAgeProfile> = {
  '6-8': completeAgeProfiles['6-8'],
  '9-11': completeAgeProfiles['9-11'],
  '12-14': completeAgeProfiles['12-14'],
  '15-18': completeAgeProfiles['15-18']
};

export const ageThemes: Record<string, AgeTheme> = {
  '6-8': completeAgeProfiles['6-8'].theme,
  '9-11': completeAgeProfiles['9-11'].theme,
  '12-14': completeAgeProfiles['12-14'].theme,
  '15-18': completeAgeProfiles['15-18'].theme
};

export const ageContent: Record<string, AgeAdaptiveContent> = {
  '6-8': completeAgeProfiles['6-8'].content,
  '9-11': completeAgeProfiles['9-11'].content,
  '12-14': completeAgeProfiles['12-14'].content,
  '15-18': completeAgeProfiles['15-18'].content
};